import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import * as actions from './actions';
import { useActions } from 'hooks/useActions';
import ProfileSettings from './ProfileSettings';
import CompanySettings from './CompanySettings';
import DepartmentSettings from './DepartmentSettings';
import EmailTemplateSettings from './EmailTemplateSettings';
import TeamSettings from './TeamSettings';
import PrivacySettings from './PrivacySettings';
import IntegrationSettings from './IntegrationSettings';
import RecruitmentStagesSettings from './RecruitmentStagesSettings';
import CandidateRatingWeightsSettings from './CandidateRatingWeightsSettings';
import CandidateRatingAreasSettings from './CandidateRatingAreasSettings';
import DemoAccountBanner from 'components/DemoAccountBanner/DemoAccountBanner';
import { Button } from 'components/Button/RoundButton';
import { Container, Wrapper } from 'components/Layout/Layout';
import AnchorContent from 'components/AnchorContent/AnchorContent';
import { TopHeadingContainer, Header, AnchorLink } from './styles';
import { getConfig } from 'functions/getConfig';
import { Spinner } from 'components/Spinner/Spinner';
import { userRoles } from 'constants/users';
import Path from 'routes/Path';

export default React.memo(() => {
  const config = getConfig();
  const navigate = useNavigate();
  const { loadSettings, resetUpdatedIds, updateCompany } = useActions(actions);

  let {
    fetchSettings,
    error,
    settings,
    updateCompanySuccess,
    updatedDepartmentId,
    newDepartmentId,
    agreementId,
    leverUserId,
    TTUserId,
  } = useSelector((state) => state.SettingsPageReducer);
  let { userRole, company } = useSelector((state) => state.auth);

  useEffect(() => {
    if (error && !error.includes('internal error')) toast.error(error);
  }, [error]);

  useEffect(() => {
    if (userRole !== userRoles.EMPLOYEE) loadSettings();
  }, [userRole, loadSettings]);

  useEffect(() => {
    if (updateCompanySuccess) {
      toast.success('Company updated successfully');
      resetUpdatedIds();
    }
  }, [updateCompanySuccess, resetUpdatedIds]);

  useEffect(() => {
    if (updatedDepartmentId) {
      toast.success('Department updated successfully');
      resetUpdatedIds();
    }
  }, [updatedDepartmentId, resetUpdatedIds]);

  useEffect(() => {
    if (newDepartmentId) toast.success('Department created successfully');
    resetUpdatedIds();
  }, [newDepartmentId, resetUpdatedIds]);

  useEffect(() => {
    if (agreementId) toast.success('New privacy agreement published');
    resetUpdatedIds();
  }, [agreementId, resetUpdatedIds]);

  useEffect(() => {
    if (TTUserId) toast.success('TeamTailor integration user successfully created');
    resetUpdatedIds();
  }, [TTUserId, resetUpdatedIds]);

  useEffect(() => {
    if (leverUserId) toast.success('Lever integration information successfully updated');
    resetUpdatedIds();
  }, [leverUserId, resetUpdatedIds]);

  if (userRole && userRole === userRoles.CANDIDATE) navigate(Path.profilePage);

  let profileSettingsAnchorPoint = [
    {
      label: 'Profile',
      component: userRole && <ProfileSettings />,
      icon: ' fa fa-user',
    },
  ];

  let anchorPoints = [
    {
      label: 'Company',
      component: settings.company && <CompanySettings userRole={userRole} />,
      icon: 'fa-brands fa-fort-awesome',
    },
    {
      label: 'Department',
      component: settings.departments && <DepartmentSettings />,
      icon: 'fa fa-building',
    },
    {
      label: 'Team',
      component: settings.teamMembers && <TeamSettings />,
      icon: 'fa fa-user-group',
    },
    {
      label: 'Email Template',
      component: settings.emails && <EmailTemplateSettings />,
      icon: 'fa fa-envelope',
    },
    {
      label: 'Privacy',
      component: settings.GDPRAgreement && <PrivacySettings userRole={userRole} companyId={company?._id} />,
      icon: 'fa fa-shield',
    },
    {
      label: 'Integrations',
      component: settings.company && <IntegrationSettings />,
      icon: 'fa fa-cogs',
    },
  ];

  let anchorPointsForNonTestsOnlyCompany = [
    {
      label: 'Recruitment Stages',
      component: settings?.company?.stages && (
        <RecruitmentStagesSettings
          recruitmentStages={settings.company.stages}
          updateStages={updateCompany}
          id={settings.company._id}
        />
      ),
      icon: 'fa fa-arrow-right',
    },
    {
      label: 'Candidate Ratings',
      component: settings.company && (
        <CandidateRatingAreasSettings
          candidateRatingAreas={settings.company.candidateRatings}
          updateCandidateRatingAreas={updateCompany}
          id={settings.company._id}
        />
      ),
      icon: 'fa fa-star',
    },
    {
      label: 'Rating Weights',
      component: settings.company && (
        <CandidateRatingWeightsSettings
          candidateRatingWeight={settings.company.ratingWeight}
          updateCandidateRatingWeight={updateCompany}
          id={settings.company._id}
        />
      ),
      icon: 'fa fa-tasks',
    },
  ];

  if (userRole) {
    anchorPoints.splice(0, 0, ...profileSettingsAnchorPoint);
    if (!company?.testsOnly) anchorPoints.splice(4, 0, ...anchorPointsForNonTestsOnlyCompany);
    if (userRole === userRoles.EMPLOYEE) anchorPoints = profileSettingsAnchorPoint;
  }

  return (
    <Wrapper padding="25px 0 0 0" minHeight="100vh">
      <Container column>
        <TopHeadingContainer>
          <Header>Settings</Header>
          {userRole !== userRoles.EMPLOYEE && (
            <AnchorLink href={`${config.REDIRECTION_URL}/gdpr-candidates`}>
              <Button primaryBlue primaryGreenBorder removeMargin>
                Candidates to be deleted
              </Button>
            </AnchorLink>
          )}
        </TopHeadingContainer>
        {settings?.company?.freeDemo && <DemoAccountBanner userRole={userRole} />}
        {fetchSettings && <Spinner />}
        <AnchorContent anchorPoints={anchorPoints} hideOnSmallDevice={true}></AnchorContent>
      </Container>
    </Wrapper>
  );
});
